import {Button, Dialog, Grid, TablePagination, Stack, Typography, Box, Paper, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Collapse, Chip, Divider} from "@mui/material";
import React, {useEffect, useState} from "react";
import LogbookApi from "../../../../api/staffApi/logbookApi";
import {useSelector} from "../../../../redux/store";
import {Delete, Edit, KeyboardArrowDown, KeyboardArrowRight, History, PostAdd, MoreHoriz} from "@mui/icons-material";
import floatParser from "../../../../helpers/floatParser";
import formatNumber from "../../../../helpers/formatNumber";
import dayjs from "dayjs";
import HesapKayitForm, {defaultHesapKayitValues, IHesapKayitFormValues} from '../shared/HesapKayitForm';

function Row(props: { row: any }) {
    const {row} = props;
    const [open, setOpen] = React.useState(false);
    const sabitler = useSelector(state => state.app.sabitler);

    const hasDetails = (row.satislar && row.satislar.length > 0) ||
                       (row.mesailer && row.mesailer.length > 0) || 
                       (row.yapilanHarcamalar && row.yapilanHarcamalar.length > 0) ||
                       (row.yapilanOdemeler && row.yapilanOdemeler.length > 0) ||
                       (row.hesaptanTransferler && row.hesaptanTransferler.length > 0) ||
                       (row.hesabaTransferler && row.hesabaTransferler.length > 0);

    return (
        <React.Fragment>
            <TableRow sx={{ 
                '& > *': { borderBottom: 'unset' },
                bgcolor: row.duzeltme ? 'warning.light' : 'transparent',
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.01)' }
            }}>
                <TableCell>
                    {hasDetails && (
                        <IconButton size="small" onClick={() => setOpen(!open)} sx={{ color: 'primary.main' }}>
                            {open ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>{row.tarih}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, color: floatParser(row.degisim) >= 0 ? 'success.main' : 'error.main' }}>
                    {formatNumber(floatParser(row.degisim))}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 900 }}>{formatNumber(row.son_bakiye)}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2, p: 2, bgcolor: 'grey.50', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.05)' }}>
                            <Typography variant="overline" fontWeight={800} color="textSecondary" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MoreHoriz fontSize="small" /> İşlem Detayları
                            </Typography>
                            
                            <Stack spacing={1}>
                                {row.satislar?.map((kayit: any, i: number) => (
                                    <DetailRow key={i} label="Satış" value={kayit.miktar} desc="Günlük Satış Kaydı" color="success.main" />
                                ))}
                                {row.mesailer?.map((kayit: any, i: number) => (
                                    <DetailRow key={i} label="Mesai" value={kayit.ucret} desc={`${kayit.tam_gun ? 'Tam Gün' : kayit.saat + ' Saat'}`} color="info.main" />
                                ))}
                                {row.yapilanHarcamalar?.map((kayit: any, i: number) => (
                                    <DetailRow key={i} label="Harcama" value={kayit.miktar} desc={sabitler?.gider_tipleri?.find(tip => tip.id === kayit.tip)?.tip ?? kayit.tip} color="error.main" />
                                ))}
                                {row.yapilanOdemeler?.map((kayit: any, i: number) => (
                                    <DetailRow key={i} label="Ödeme" value={kayit.miktar} desc={kayit.tip} color="success.main" />
                                ))}
                                {row.hesaptanTransferler?.map((kayit: any, i: number) => (
                                    <DetailRow key={i} label="Transfer/Ödeme" value={kayit.miktar} desc={kayit.hedef_adi || kayit.aciklama} color="error.main" />
                                ))}
                                {row.hesabaTransferler?.map((kayit: any, i: number) => (
                                    <DetailRow key={i} label="Gelen Transfer" value={kayit.miktar} desc={kayit.kaynak_adi || kayit.aciklama} color="success.main" />
                                ))}
                            </Stack>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

const DetailRow = ({ label, value, desc, color }: any) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'white', borderRadius: '6px' }}>
        <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" fontWeight={800} sx={{ color }}>{label.toUpperCase()}</Typography>
            <Typography variant="body2" color="textSecondary">{desc}</Typography>
        </Stack>
        <Typography variant="body2" fontWeight={800}>{formatNumber(value)}</Typography>
    </Box>
);

const HesapDetay = ({hesapId}: { hesapId: number }) => {
    const [hesap, setHesap] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hesapPage, setHesapPage] = useState(1);
    const [hesapRowsPerPage, setHesapRowsPerPage] = useState(25);
    const [kayitEkle, setKayitEkle] = useState<boolean>(false);
    const [kayitGuncelle, setKayitGuncelle] = useState<boolean>(false);
    const [kayit, setKayit] = useState<IHesapKayitFormValues>(defaultHesapKayitValues(hesapId));

    const bilgileriGuncelle = (res: any) => {
        setHesap(res.gunluk_kayitlar);
        setTotalCount(res.total_count);
    }

    const handleHesapChangePage = (event: unknown, newPage: number) => setHesapPage(newPage + 1);
    const handleHesapChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setHesapRowsPerPage(parseInt(event.target.value, 10));
        setHesapPage(1);
    };

    useEffect(() => {
        if (hesapId > 0) {
            LogbookApi.getHesapKayit(hesapId, hesapPage, hesapRowsPerPage)
                .then(res => bilgileriGuncelle(res))
                .catch(e => console.warn(e));
        }
    }, [hesapId, hesapPage, hesapRowsPerPage]);

    return (
        <Box sx={{ width: '100%' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <History color="primary" />
                    <Typography variant="h6" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Hesap Hareketleri</Typography>
                </Stack>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<PostAdd />}
                    sx={{ borderRadius: '10px', fontWeight: 800 }}
                    onClick={() => {
                        setKayit(defaultHesapKayitValues(hesapId));
                        setKayitEkle(true);
                    }}
                >
                    Düzeltme Ekle
                </Button>
            </Stack>

            <Paper sx={{ borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: 'none', overflow: 'hidden' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ width: 60 }} />
                            <TableCell align="center" sx={{ fontWeight: 800 }}>Tarih</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>Değişim</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>Son Bakiye</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(hesap ?? []).map((row: any) => (
                            !row.duzeltme ? (
                                <Row key={row.id} row={row} />
                            ) : (
                                <TableRow key={row.id} sx={{ bgcolor: 'warning.light', '&:hover': { bgcolor: 'warning.main', opacity: 0.9 } }}>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton size="small" onClick={() => { setKayit({...row}); setKayitGuncelle(true); }}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => LogbookApi.deleteHesapKayit({id: row.id}).then(res => bilgileriGuncelle(res))}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>{row.tarih}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 800, color: floatParser(row.degisim) >= 0 ? 'success.main' : 'error.main' }}>
                                        {formatNumber(floatParser(row.degisim))}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 900 }}>{formatNumber(row.son_bakiye)}</TableCell>
                                </TableRow>
                            )
                        ))}
                    </TableBody>
                </Table>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={hesapRowsPerPage}
                    page={hesapPage - 1}
                    onPageChange={handleHesapChangePage}
                    onRowsPerPageChange={handleHesapChangeRowsPerPage}
                    sx={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}
                />
            </Paper>

            <Dialog open={kayitEkle} onClose={() => setKayitEkle(false)} PaperProps={{ sx: { borderRadius: '20px' } }}>
                <HesapKayitForm
                    kayit={kayit}
                    setKayit={setKayit}
                    onClose={() => setKayitEkle(false)}
                    onSuccess={(res: any) => bilgileriGuncelle(res)}
                    mode="add"
                />
            </Dialog>

            <Dialog open={kayitGuncelle} onClose={() => setKayitGuncelle(false)} PaperProps={{ sx: { borderRadius: '20px' } }}>
                <HesapKayitForm
                    kayit={kayit}
                    setKayit={setKayit}
                    onClose={() => setKayitGuncelle(false)}
                    onSuccess={(res: any) => bilgileriGuncelle(res)}
                    mode="edit"
                />
            </Dialog>
        </Box>
    );
};

export default HesapDetay;
